import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

/**
 * Images above this size are re-encoded before upload. The nginx in front of
 * some environments (test) keeps the 1 MB default body limit and answers 413,
 * so a 2 MB product photo that uploads fine on prod silently fails there.
 */
const MAX_UPLOAD_BYTES = 900 * 1024;
const MAX_UPLOAD_EDGE = 1600;

/** Downscale/re-encode a large raster image so it fits under MAX_UPLOAD_BYTES. */
export async function shrinkImage(file: File): Promise<File> {
  if (file.size <= MAX_UPLOAD_BYTES || !/^image\/(png|jpe?g|webp)$/i.test(file.type)) return file;
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  let blob: Blob | null = null;
  for (const quality of [0.85, 0.7, 0.55]) {
    blob = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/webp', quality));
    if (blob && blob.size <= MAX_UPLOAD_BYTES) break;
  }
  if (!blob || blob.size >= file.size) return file;
  return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.webp', { type: 'image/webp' });
}

/**
 * Applies shrinkImage to every image in every multipart upload, so each
 * uploader (items, categories, brands, options, gallery, catering, services)
 * gets it without having to remember to.
 */
@Injectable()
export class ImageUploadInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const body = req.body;
    if (!(body instanceof FormData)) return next.handle(req);
    const entries: [string, FormDataEntryValue][] = [];
    body.forEach((v, k) => entries.push([k, v]));
    if (!entries.some(([, v]) => v instanceof File && v.size > MAX_UPLOAD_BYTES)) {
      return next.handle(req);
    }
    const shrinking = Promise.all(entries.map(async ([k, v]): Promise<[string, FormDataEntryValue]> =>
      [k, v instanceof File ? await shrinkImage(v) : v]));
    return from(shrinking).pipe(switchMap((shrunk: [string, FormDataEntryValue][]) => {
      const form = new FormData();
      for (const [k, v] of shrunk) {
        if (v instanceof File) form.append(k, v, v.name);
        else form.append(k, v);
      }
      return next.handle(req.clone({ body: form }));
    }));
  }
}
